import type { RefObject } from "react";
import { NumPad, NumPadPop, PhonePad, TextPad } from "../shared";
import type { BookingFormProps } from "./bookingFormProps";

/** Какое поле формы сейчас вводится своим падом (null — все закрыты). */
export type PadField = "players" | "phone" | "dep" | "tg" | "comment" | null;

/**
 * BookingFormPads — пады ввода полей BookingForm (A46/БМ5; A46-коррекция
 * 2026-08-12: имя клиента и комментарий ДК вернулись на системную клавиатуру
 * и вводятся в самой форме — их TextPad-ветки сняты).
 *
 * Соответствие полей (БМ6): Кол-во игроков → NumPadPop int «чел» (M.5 у поля,
 * решение 13.08); Здтк-сумма → NumPad money (max 100000); Телефон → PhonePad-
 * ПОПОВЕР у поля (крупный M.5 со вставкой из буфера, дополнение 13.08; канон
 * «8»+10 цифр не менялся); @telegram/ID → TextPad en-слой.
 * Лимиты полей (max/maxLength) — прежние атрибуты input'ов, перенесены 1:1.
 *
 * Каждый commit диффается (old !== new): неизменённое значение НЕ дёргает
 * хост-колбэки — авто-конвертация лид→бронь (bookify) и сброс validationErr
 * не срабатывают от простого открытия-закрытия пада. Модели коммита — как у
 * модулей: NumPad неявная (любое закрытие), поповеры NumPadPop/PhonePad —
 * тап мимо/✓ (Esc — отмена), TextPad явная («Готово»).
 */
export function BookingFormPads({ p, pad, anchor, commentRef, onClose, onDockHeight }: {
  p: BookingFormProps;
  pad: PadField;
  /** Якорь поля для поповер-падов (M.5); null — пад-шиты без якоря. */
  anchor: HTMLElement | null;
  /** 06.09 (владелец): поле комментария — хост docked-пада (тап по нему пад не закрывает). */
  commentRef?: RefObject<HTMLTextAreaElement | null>;
  onClose: () => void; onDockHeight?: (h: number) => void }) {
  return (
    <>
      <NumPadPop
        open={pad === "players"}
        anchor={anchor}
        label="Количество игроков"
        mode="int"
        max={100}
        value={p.players}
        onCommit={(v) => {
          const s = v ? String(v) : "";
          if (s !== p.players) p.onPlayersChange(s);
        }}
        onClose={onClose}
      />
      <NumPad
        open={pad === "dep"}
        label="Задаток"
        mode="money"
        max={100000}
        value={p.prepaidAmt}
        onCommit={(v) => {
          if (v !== p.prepaidAmt) p.onPrepaidAmtChange(v);
        }}
        onClose={onClose}
      />
      <PhonePad
        open={pad === "phone"}
        anchor={anchor}
        label="Телефон клиента"
        value={p.phone}
        onCommit={(v) => {
          if (v !== p.phone) p.onPhoneChange(v);
        }}
        onClose={onClose}
      />
      <TextPad
        open={pad === "tg"}
        label="Telegram"
        startLayer="en"
        placeholder="@telegram или ID…"
        value={p.tg}
        onCommit={(v) => {
          if (v !== p.tg) p.onTgChange(v);
        }}
        onClose={onClose}
      />
      {/* Комментарий ДК — docked M.6 (14.08): live-ввод прямо в поле, без шторы; hostRef — само поле (06.09). */}
      <TextPad
        open={pad === "comment"}
        docked
        hostRef={commentRef}
        multiline
        numRow
        label="Комментарий ДК"
        value={p.comment}
        onChange={(v) => p.onCommentChange(v)}
        onDockHeight={onDockHeight}
        onClose={onClose}
      />
    </>
  );
}
